import { useEffect, useState } from 'react';
import {
  collection,
  documentId,
  getDocs,
  orderBy,
  query,
  where,
  limit as fsLimit,
} from 'firebase/firestore';
import {
  AlertSchema,
  CohortRetentionSchema,
  DailyAggregateSchema,
  type Alert,
  type CohortRetention,
  type DailyAggregate,
} from '@etiquetachef/types';
import { firestore } from '@/lib/firebase';
import { reportNonFatal, serializeError } from '@/lib/errorReport';

const TAG = 'admin/reports/useMonthlyReport';

export type MonthlyReport = {
  yyyymm: string;
  series: DailyAggregate[];
  cohort: CohortRetention | null;
  highAlerts: Alert[];
  // Sumário derivado
  mrrEndUsdCents: number;
  netNewMrrUsdCents: number;
  newTenants: number;
  churnedTenants: number;
  churnRatePct: number;
};

export type MonthlyReportState =
  | { kind: 'LOADING' }
  | { kind: 'ERROR'; error: string }
  | { kind: 'READY'; report: MonthlyReport };

/** Primeiro e último dia (`yyyymmdd`, UTC) do mês `yyyymm` — limites do range por id. */
function firstAndLastDayOf(yyyymm: string): { from: string; to: string } {
  const yyyy = Number(yyyymm.slice(0, 4));
  const mm = Number(yyyymm.slice(4, 6));
  const last = new Date(Date.UTC(yyyy, mm, 0)).getUTCDate();
  const mmStr = String(mm).padStart(2, '0');
  return {
    from: `${String(yyyy)}${mmStr}01`,
    to: `${String(yyyy)}${mmStr}${String(last).padStart(2, '0')}`,
  };
}

/**
 * Carrega e resume os dados do relatório mensal de BI (PDF) de um mês.
 *
 * Origem: sem issue — commit fff721034, 2026-05-04 18:55 +0200 (rota /reports + PDF mensal).
 *
 * Por quê: o PDF é montado no navegador a partir dos agregados que o BI já
 * grava; não há callable de relatório — o resumo (MRR final, net new, novos,
 * cancelados, churn) é derivado aqui da série diária.
 *
 * Contrato: `LOADING` a cada troca de `yyyymm`; leitura única (`getDocs`, sem
 * listener); `READY` com a série ordenada por dia, a coorte do mês (ou `null`)
 * e os alertas HIGH cujo `periodAffected` cruza o mês; `ERROR` com a mensagem
 * crua. Resultado de mês anterior descartado se o mês mudar no meio (guard).
 *
 * Usado por: `features/reports/ReportsPage.tsx` (`ExportSelectedMonth`).
 *
 * Arquitetura: BI é derivado, nunca fonte da verdade (`match
 * /aggregations/{type}/{subcoll}/{id}` e `/aggregations/alerts/items`, leitura
 * só `isSuperAdmin()`; escrita só pelos schedulers de agregação).
 *
 * Dados (três leituras em paralelo):
 *  - `aggregations/daily/days/{yyyymmdd}` por range de `documentId()` —
 *    `DailyAggregateSchema`; valores monetários em **centavos de USD**
 *    (`mrr.usdEquivalentCents`, `netNewMrr.netUsdCents`);
 *  - `aggregations/cohorts/months/{yyyymm}` — `CohortRetentionSchema`;
 *  - `aggregations/alerts/items` com `severity == 'HIGH'` + `orderBy('observedAt',
 *    'desc')` — índice composto `items (severity ASC, observedAt DESC)` em
 *    `firebase/firestore.indexes.json`; `AlertSchema`. Todos os alertas HIGH
 *    são lidos e o recorte do mês é no cliente.
 * Doc fora do schema é descartado em silêncio.
 *
 * Dicas: "cancelados" = soma de `tenants.suspendedToday`, e o churn é sobre
 * `tenants.active` do último dia do mês — não sobre a base do início.
 */
export function useMonthlyReport(yyyymm: string): MonthlyReportState {
  const [state, setState] = useState<MonthlyReportState>({ kind: 'LOADING' });

  useEffect(() => {
    const guard = { cancelled: false };
    setState({ kind: 'LOADING' });

    /** Busca as três fontes, filtra e deriva o sumário; respeita o guard. */
    async function load(): Promise<void> {
      try {
        const range = firstAndLastDayOf(yyyymm);
        const [seriesSnap, cohortSnap, alertsSnap] = await Promise.all([
          getDocs(
            query(
              collection(firestore, 'aggregations', 'daily', 'days'),
              where(documentId(), '>=', range.from),
              where(documentId(), '<=', range.to),
            ),
          ),
          getDocs(
            query(
              collection(firestore, 'aggregations', 'cohorts', 'months'),
              where(documentId(), '==', yyyymm),
              fsLimit(1),
            ),
          ),
          getDocs(
            query(
              collection(firestore, 'aggregations', 'alerts', 'items'),
              where('severity', '==', 'HIGH'),
              orderBy('observedAt', 'desc'),
            ),
          ),
        ]);

        if (guard.cancelled) return;

        const series: DailyAggregate[] = [];
        for (const d of seriesSnap.docs) {
          const parsed = DailyAggregateSchema.safeParse(d.data());
          if (parsed.success) series.push(parsed.data);
        }
        series.sort((a, b) => a.yyyymmdd.localeCompare(b.yyyymmdd));

        const cohort = cohortSnap.docs[0]
          ? CohortRetentionSchema.safeParse(cohortSnap.docs[0].data()).data ?? null
          : null;

        const highAlerts: Alert[] = [];
        const yyyymmStart = yyyymm + '01';
        const yyyymmEnd = range.to;
        for (const d of alertsSnap.docs) {
          const parsed = AlertSchema.safeParse(d.data());
          if (!parsed.success) continue;
          const a = parsed.data;
          // Filtra alertas cujo período cai no mês.
          if (
            a.periodAffected.toYyyymmdd < yyyymmStart ||
            a.periodAffected.fromYyyymmdd > yyyymmEnd
          ) {
            continue;
          }
          highAlerts.push(a);
        }

        const last = series[series.length - 1];
        const mrrEnd = last?.mrr.usdEquivalentCents ?? 0;
        let netNew = 0;
        let newT = 0;
        let churned = 0;
        for (const d of series) {
          netNew += d.netNewMrr.netUsdCents;
          newT += d.tenants.createdToday;
          churned += d.tenants.suspendedToday;
        }
        const activeAtEnd = last?.tenants.active ?? 0;
        const churnRate = activeAtEnd === 0 ? 0 : churned / activeAtEnd;

        setState({
          kind: 'READY',
          report: {
            yyyymm,
            series,
            cohort,
            highAlerts,
            mrrEndUsdCents: mrrEnd,
            netNewMrrUsdCents: netNew,
            newTenants: newT,
            churnedTenants: churned,
            churnRatePct: churnRate,
          },
        });
      } catch (err) {
        if (guard.cancelled) return;
        console.error(`${TAG}: failed`, err);
        // #1021 (2026-10-23): a falha só ia para o console — invisível em produção (sem DevTools do cliente).
        reportNonFatal('admin_use_monthly_report_failed', {
          site: 'failed',
          code: (err as { code?: string }).code ?? null,
          ...serializeError(err),
        });
        setState({
          kind: 'ERROR',
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
    void load();
    return (): void => {
      guard.cancelled = true;
    };
  }, [yyyymm]);

  return state;
}
